// Crayon's own icons: drawn by hand on a 24-unit grid in crooked strokes, the
// way its artboard draws them — My Team's shirt, the football, the trophy,
// the player, the bell, the chevrons and the menu lines are the artboard's own
// paths (docs/design/mockups/looks/Crayon.dc.html); the rest are drawn to
// match. The look's stylesheet runs every one through its wobble. A meaning
// not drawn here is Clean's shape, wobbled the same way.

import type { IconName } from '../../kit/Icon';
import type { Drawing, DrawingProps } from '../define';

type Stroke =
  string | { d: string; fill: true } | { circle: [number, number, number]; fill?: true };

function hand(strokes: Stroke[]): Drawing {
  function HandDrawn({ size = 24, className, ...rest }: DrawingProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...rest}
      >
        {strokes.map((s, i) =>
          typeof s === 'string' ? (
            <path key={i} d={s} />
          ) : 'd' in s ? (
            <path key={i} d={s.d} fill="currentColor" />
          ) : (
            <circle
              key={i}
              cx={s.circle[0]}
              cy={s.circle[1]}
              r={s.circle[2]}
              fill={s.fill ? 'currentColor' : 'none'}
            />
          ),
        )}
      </svg>
    );
  }
  return HandDrawn;
}

const SHIRT = hand([
  'M8.5 3.8 C 10 5.6, 14 5.6, 15.5 3.8 L 20.2 6.6 L 18.4 10.4 L 16.3 9.4 L 16.5 20.2 C 13.5 20.6, 10.5 20.6, 7.6 20.2 L 7.8 9.4 L 5.6 10.4 L 3.8 6.8 Z',
]);
const FOOTBALL = hand([
  'M4.2 19.6 C 3 13, 8.4 4.6, 19.7 4.3 C 20.9 11, 15.4 19.4, 4.2 19.6 Z',
  'M9.2 14.6 L 14.8 9.2',
  'M10.2 11.8 L 12.2 13.8 M 11.8 10.2 L 13.8 12.2',
]);
const TROPHY = hand([
  'M7.6 4.2 L 16.4 4 C 16.6 9.5, 15.4 13.2, 12 13.6 C 8.6 13.3, 7.4 9.6, 7.6 4.2 Z',
  'M7.8 6 C 5 5.8, 4.4 6.6, 4.6 8 C 4.9 9.8, 6.4 10.6, 8.2 10.4',
  'M16.2 6 C 19 5.8, 19.6 6.6, 19.4 8 C 19.1 9.8, 17.6 10.6, 15.8 10.4',
  'M12 13.8 L 12 17',
  'M9.2 17.1 C 11 16.9, 13 17, 14.8 17.1 L 15.4 20.2 L 8.6 20.3 Z',
]);
const PERSON = hand([
  'M12 4 C 14.2 4, 15.8 5.7, 15.7 7.9 C 15.6 10.1, 14 11.6, 12 11.6 C 9.9 11.6, 8.3 10, 8.3 7.8 C 8.3 5.6, 9.9 4.1, 12.4 4.1',
  'M4.6 20.3 C 4.9 16, 8 13.8, 12 13.8 C 16 13.8, 19.2 16, 19.4 20.2',
]);
const PEOPLE = hand([
  'M9 5 C 10.9 5, 12.2 6.4, 12.1 8.3 C 12 10.2, 10.7 11.4, 9 11.4 C 7.2 11.4, 5.9 10.1, 5.9 8.2 C 5.9 6.3, 7.2 5.1, 9.4 5.1',
  'M3 19.8 C 3.2 16.2, 5.6 14.2, 9 14.2 C 12.4 14.2, 14.8 16.2, 15 19.7',
  'M15.4 5.4 C 17 5.3, 18.2 6.6, 18.1 8.3 C 18 9.8, 17 10.9, 15.6 11',
  'M17.2 14.4 C 19.4 14.9, 21 16.6, 21.1 19.6',
]);
const LINES = hand([
  'M4.5 7.2 C 9 6.6, 15 7.6, 19.6 6.9',
  'M4.3 12.2 C 9.2 11.8, 14.8 12.6, 19.7 11.9',
  'M4.6 17.1 C 9 16.7, 15.1 17.5, 19.4 16.9',
]);
const BELL = hand([
  'M5.8 16.8 C 7 15.2, 7.1 13, 7 10.6 C 6.9 7.3, 9.2 5, 12.1 5 C 15 5.1, 17.1 7.4, 17 10.6 C 16.9 13, 17 15.2, 18.3 16.7 C 14 17.3, 10 17.3, 5.8 16.8 Z',
  'M10 19.3 C 10.7 20.6, 13.3 20.7, 14.1 19.2',
  'M12.1 3.2 L 12 5',
]);
const PREVIOUS = hand(['M15.3 4.6 C 12.7 8, 10 10.4, 7.7 12.3 C 10.1 14.4, 12.8 16.8, 15.7 20']);
const NEXT = hand(['M8.7 4.6 C 11.3 8, 14 10.4, 16.3 12.3 C 13.9 14.4, 11.2 16.8, 8.3 20']);
const EXPAND = hand(['M4.8 8.8 C 7.8 11.6, 10 13.8, 12 16.6 C 14 13.8, 16.2 11.6, 19.4 8.6']);
const VS = hand([
  'M3.6 6.4 C 4.8 10, 6 13.8, 7.4 17.6 C 8.6 13.6, 9.8 10, 11.2 6.2',
  'M19.4 7.6 C 18.4 6.2, 15.2 6, 14.4 7.8 C 13.6 9.8, 16.2 10.8, 17.8 11.8 C 19.8 13, 19.6 16.2, 17.4 17.2 C 15.6 18, 13.8 17.4, 13 16',
]);
const BARS = hand([
  'M5.4 19.4 C 5.6 16.4, 5.2 13.8, 5.5 11',
  'M12 19.6 C 11.8 15, 12.2 9.6, 11.9 4.6',
  'M18.6 19.4 C 18.8 16.8, 18.4 14.6, 18.7 12.4',
  'M3.2 20.3 C 9 19.9, 15 20.5, 20.8 20',
]);
const PAGE =
  'M6 3.6 C 10 3.4, 14 3.8, 18.2 3.5 C 18.6 9, 18.2 15, 18.5 20.4 C 14 20.7, 10 20.3, 5.8 20.6 C 6.1 15, 5.6 9, 6 3.6 Z';
const RECAP = hand([
  PAGE,
  'M9 8 C 11 7.8, 13 8.1, 15.2 7.9',
  'M9 12 C 11 11.8, 13 12.2, 15.3 11.9',
  'M9 16 C 10.4 15.9, 11.6 16.1, 13 16',
]);
const NEWS = hand([
  PAGE,
  'M8.6 7.4 C 10 7.2, 11.4 7.5, 12.4 7.3 L 12.3 11 C 11.2 11.2, 9.8 11, 8.7 11.1 Z',
  'M14.2 8 C 14.8 7.9, 15.4 8, 15.9 7.9',
  'M14.2 10.6 C 14.8 10.5, 15.4 10.7, 15.9 10.6',
  'M8.8 14.4 C 11.2 14.2, 13.6 14.5, 15.9 14.3',
  'M8.8 17.2 C 10.8 17, 12.8 17.3, 14.6 17.1',
]);
const WAIVERS = hand([
  'M5 9.4 C 6.2 5.8, 10 4, 13.6 4.6 C 16 5, 17.8 6.4, 18.8 8.2',
  'M19.4 4.6 C 19.2 6, 19 7.2, 18.9 8.4 C 17.6 8.4, 16.4 8.6, 15.2 8.8',
  'M19 14.6 C 17.8 18.2, 14 20, 10.4 19.4 C 8 19, 6.2 17.6, 5.2 15.8',
  'M4.6 19.4 C 4.8 18, 5 16.8, 5.1 15.6 C 6.4 15.6, 7.6 15.4, 8.8 15.2',
]);
const TRADE = hand([
  'M4.4 8.2 C 9.4 7.8, 14.4 8.4, 19.4 8',
  'M16 4.8 C 17.2 5.9, 18.4 7, 19.6 8.1 C 18.4 9.2, 17.2 10.3, 16 11.4',
  'M19.6 16 C 14.6 15.6, 9.6 16.2, 4.6 15.8',
  'M8 12.6 C 6.8 13.7, 5.6 14.8, 4.4 15.9 C 5.6 17, 6.8 18.1, 8 19.2',
]);
const STANDINGS = hand([
  'M4.6 6 C 5.2 5.4, 5.8 4.9, 6.4 4.4 L 6.3 9.6',
  'M4.4 14.2 C 4.8 12.8, 7.4 12.8, 7.2 14.4 C 7 15.6, 5.4 16.6, 4.4 18.2 C 5.4 18.1, 6.6 18.2, 7.6 18.1',
  'M10.4 7 C 13.4 6.7, 16.6 7.2, 19.8 6.9',
  'M10.4 12.2 C 13.4 11.9, 16.6 12.4, 19.8 12.1',
  'M10.4 17.2 C 13.4 16.9, 16.6 17.4, 19.8 17.1',
]);
const CALENDAR = hand([
  'M4.4 6.4 C 9.4 6, 14.6 6.5, 19.6 6.2 C 19.9 11, 19.5 15.4, 19.8 20 C 14.6 20.3, 9.4 19.9, 4.3 20.2 C 4.6 15.6, 4.2 11, 4.4 6.4 Z',
  'M4.6 10.2 C 9.6 9.9, 14.6 10.4, 19.6 10.1',
  'M8.4 3.8 L 8.5 7.6',
  'M15.6 3.8 L 15.5 7.6',
]);
const HISTORY = hand([
  'M5 10 C 6 7, 8.6 4.6, 12.4 4.2 C 16.8 4.4, 20 7.8, 19.8 12.2 C 19.6 16.6, 16.2 19.8, 11.8 19.7 C 8.2 19.6, 5.4 17.2, 4.6 13.8',
  'M4.4 6.4 C 4.4 7.8, 4.6 9.2, 4.8 10.4 C 6 10.2, 7.2 10, 8.4 9.8',
  'M12 8.2 L 12.1 12.4 L 15 14.2',
]);
const BOOK = hand([
  'M12 6.4 C 9.4 4.8, 6.6 4.6, 4 5.4 C 4.2 10.4, 4 15.2, 4.2 19.4 C 6.8 18.6, 9.6 18.8, 12 20.2 C 14.4 18.8, 17.2 18.6, 19.8 19.4 C 20 15, 19.8 10.2, 20 5.4 C 17.4 4.6, 14.6 4.8, 12 6.4 Z',
  'M12 6.4 C 11.8 11, 12.2 15.6, 12 20.2',
]);
const PALETTE = hand([
  'M12 4 C 16.8 3.8, 20.4 7.2, 20.2 11.6 C 20 14.4, 17.8 15, 16 14.6 C 14.2 14.2, 13 15.6, 13.8 17.2 C 14.6 18.8, 13.6 20.4, 11.6 20.2 C 7.2 19.8, 3.8 16.4, 3.8 12 C 3.8 7.6, 7.4 4.2, 12 4 Z',
  { circle: [8.2, 10.6, 1.1], fill: true },
  { circle: [11.6, 7.6, 1.1], fill: true },
  { circle: [15.6, 9, 1.1], fill: true },
]);
const CHAT = hand([
  'M4.4 6.2 C 9.4 5.6, 14.8 5.8, 19.6 6.2 C 20 9.6, 19.8 12.6, 19.4 15.2 C 16 15.4, 12.6 15.2, 9.6 15.4 C 8 16.8, 6.6 18, 4.8 19 C 5.4 17.8, 5.8 16.6, 5.9 15.3 L 4.6 15.3 C 4.2 12.2, 4 9.2, 4.4 6.2 Z',
]);
const SEARCH = hand([
  'M10.6 4.2 C 14.2 4.1, 16.8 6.8, 16.7 10.3 C 16.6 13.8, 13.8 16.4, 10.4 16.3 C 6.9 16.2, 4.4 13.5, 4.5 10.1 C 4.6 6.8, 7.1 4.4, 11.2 4.3',
  'M15.2 15.2 C 16.8 16.8, 18.4 18.4, 20.2 20.2',
]);
const GRID = hand([
  'M4.4 4.6 C 6.4 4.4, 8.4 4.7, 10.4 4.5 L 10.3 10.4 C 8.4 10.6, 6.4 10.3, 4.5 10.5 Z',
  'M13.6 4.4 C 15.6 4.6, 17.6 4.3, 19.6 4.5 L 19.5 10.5 C 17.6 10.3, 15.6 10.6, 13.7 10.4 Z',
  'M4.5 13.6 C 6.4 13.4, 8.4 13.7, 10.4 13.5 L 10.5 19.5 C 8.4 19.7, 6.4 19.4, 4.4 19.6 Z',
  'M13.7 13.5 C 15.6 13.7, 17.6 13.4, 19.5 13.6 L 19.6 19.4 C 17.6 19.6, 15.6 19.3, 13.6 19.5 Z',
]);
const PLUS = hand([
  'M12.2 4.6 C 11.9 9.6, 12.2 14.4, 11.9 19.4',
  'M4.6 12.1 C 9.6 11.8, 14.4 12.3, 19.4 11.9',
]);
const MINUS = hand(['M4.6 12.1 C 9.6 11.8, 14.4 12.3, 19.4 11.9']);
const CROSS = hand([
  'M5.6 5.8 C 9.8 9.6, 14 14, 18.4 18.2',
  'M18.2 5.6 C 14 9.8, 9.8 14, 5.8 18.4',
]);
const CHECK = hand(['M4.8 12.8 C 6.6 14.4, 8.2 16.2, 9.8 18.2 C 12.6 13.4, 15.8 9, 19.6 5.4']);
const PENCIL = hand([
  'M15.8 4.6 C 16.8 5.4, 18.4 6.8, 19.4 8 C 15.6 12, 11.6 16, 7.6 19.8 L 4.2 20 L 4.4 16.6 C 8.2 12.6, 12 8.6, 15.8 4.6 Z',
  'M13.6 6.8 L 17.2 10.2',
]);
const BOLT = hand([
  'M13.6 3.4 C 11.6 7, 9.6 10.4, 7.4 13.4 C 9.2 13.4, 11 13.2, 12.6 13.3 C 11.8 15.6, 11 18, 10.4 20.6 C 12.6 17.4, 14.8 14, 16.8 10.6 C 15.2 10.6, 13.6 10.8, 12.2 10.7 C 12.6 8.2, 13 5.8, 13.6 3.4 Z',
]);
const PLAY = hand([
  'M7.4 4.6 C 11.4 6.8, 15.4 9.4, 19 12 C 15.2 14.6, 11.2 17.2, 7.2 19.6 C 7.6 14.6, 7.2 9.6, 7.4 4.6 Z',
]);
const PAUSE = hand([
  'M8.4 5 C 8.2 9.6, 8.6 14.4, 8.3 19',
  'M15.6 5 C 15.8 9.6, 15.4 14.4, 15.7 19',
]);
const LOCK = hand([
  'M5.6 11 C 9.8 10.7, 14.2 11.2, 18.4 10.9 C 18.6 14, 18.4 17, 18.6 20 C 14.2 20.3, 9.8 19.9, 5.4 20.1 C 5.7 17, 5.4 14, 5.6 11 Z',
  'M8.4 10.8 C 8 6.8, 9.8 4.2, 12 4.2 C 14.4 4.2, 16 6.6, 15.6 10.8',
]);
const WARNING = hand([
  'M12 4 C 14.8 9, 17.6 14, 20.4 19.2 C 14.8 19.6, 9.2 19.4, 3.6 19.4 C 6.4 14.2, 9.2 9.2, 12 4 Z',
  'M12 9.6 C 12.1 11, 11.9 12.4, 12 13.8',
  { circle: [12, 16.6, 0.9], fill: true },
]);
const INFO = hand([
  'M12.4 3.8 C 17 4, 20.4 7.6, 20.2 12.2 C 20 16.8, 16.4 20.2, 11.8 20 C 7.2 19.8, 3.8 16.2, 4 11.6 C 4.2 7.4, 7.4 4.2, 11.4 3.9',
  'M12 11 C 12.1 12.8, 11.9 14.6, 12 16.4',
  { circle: [12, 7.9, 0.9], fill: true },
]);
const STAR_PATH =
  'M12 3.6 C 13 6.4, 13.8 8.4, 14.6 9.6 C 16.8 9.8, 18.6 9.9, 20.4 10.2 C 18.8 11.8, 17.4 13, 16.2 14.2 C 16.6 16.4, 17 18.4, 17.4 20.4 C 15.4 19.2, 13.6 18.2, 12 17.2 C 10.4 18.2, 8.6 19.2, 6.6 20.4 C 7 18.4, 7.4 16.4, 7.8 14.2 C 6.6 13, 5.2 11.8, 3.6 10.2 C 5.4 9.9, 7.2 9.8, 9.4 9.6 C 10.2 8.4, 11 6.4, 12 3.6 Z';
const STAR = hand([STAR_PATH]);
const STAR_FILLED = hand([{ d: STAR_PATH, fill: true }]);
const BACK = hand([
  'M19.6 12.2 C 14.6 11.9, 9.6 12.3, 4.6 12',
  'M9.6 6.8 C 7.8 8.6, 6.2 10.2, 4.4 12 C 6.2 13.8, 7.8 15.4, 9.6 17.2',
]);
const FORWARD = hand([
  'M4.4 12 C 9.4 12.3, 14.4 11.9, 19.4 12.2',
  'M14.4 6.8 C 16.2 8.6, 17.8 10.2, 19.6 12 C 17.8 13.8, 16.2 15.4, 14.4 17.2',
]);
const UP = hand([
  'M12.1 19.6 C 11.8 14.6, 12.2 9.6, 11.9 4.6',
  'M6.8 9.6 C 8.6 7.8, 10.2 6.2, 12 4.4 C 13.8 6.2, 15.4 7.8, 17.2 9.6',
]);
const DOWN = hand([
  'M11.9 4.4 C 12.2 9.4, 11.8 14.4, 12.1 19.4',
  'M6.8 14.4 C 8.6 16.2, 10.2 17.8, 12 19.6 C 13.8 17.8, 15.4 16.2, 17.2 14.4',
]);
const CLOCK = hand([
  'M12.4 3.8 C 17 4, 20.4 7.6, 20.2 12.2 C 20 16.8, 16.4 20.2, 11.8 20 C 7.2 19.8, 3.8 16.2, 4 11.6 C 4.2 7.4, 7.4 4.2, 11.4 3.9',
  'M12 7.6 L 12.1 12.3 L 15.2 14',
]);
const SEND = hand([
  'M4.2 11.4 C 9.4 9.2, 14.8 6.8, 20.2 4 C 18.2 9.4, 16 14.8, 13.4 20 C 12.4 17.4, 11.4 14.8, 10.4 12.8 C 8.4 12.4, 6.4 11.8, 4.2 11.4 Z',
  'M10.4 12.8 C 13.4 9.8, 16.6 7, 20 4.2',
]);
const TRASH = hand([
  'M4.4 6.6 C 9.4 6.3, 14.6 6.8, 19.6 6.5',
  'M9.2 6.4 C 9.4 5, 10.2 4.2, 12 4.2 C 13.8 4.2, 14.6 5, 14.8 6.4',
  'M6.4 6.8 C 6.8 11.2, 7 15.6, 7.4 20 C 10.4 20.3, 13.6 20.1, 16.6 20 C 17 15.6, 17.2 11.2, 17.6 6.8',
]);
const UNDO = hand([
  'M8.6 4.8 C 7.2 6.2, 5.8 7.6, 4.4 9 C 5.8 10.4, 7.2 11.8, 8.6 13.2',
  'M4.6 9 C 9 8.8, 13.4 8.8, 15.4 9.2 C 18.4 9.8, 20 12.2, 19.8 14.8 C 19.6 17.6, 17.4 19.6, 14.4 19.6 L 11 19.6',
]);
const REFRESH = hand([
  'M19.2 9.4 C 18 6.2, 15.2 4.2, 12 4.2 C 7.6 4.2, 4.2 7.8, 4.4 12.2',
  'M19.6 4.6 C 19.5 6.2, 19.4 7.8, 19.3 9.4 C 17.8 9.5, 16.2 9.4, 14.6 9.6',
  'M4.8 14.6 C 6 17.8, 8.8 19.8, 12 19.8 C 16.4 19.8, 19.8 16.2, 19.6 11.8',
  'M4.4 19.4 C 4.5 17.8, 4.6 16.2, 4.7 14.6 C 6.2 14.5, 7.8 14.6, 9.4 14.4',
]);
const SHARE = hand([
  { circle: [18, 5.8, 2.4] },
  { circle: [6, 12, 2.4] },
  { circle: [18, 18.2, 2.4] },
  'M8.2 10.9 C 10.8 9.6, 13.2 8.2, 15.8 6.9',
  'M8.2 13.1 C 10.8 14.4, 13.2 15.8, 15.8 17.1',
]);
const LOGOUT = hand([
  'M13.6 4.4 C 10.6 4.2, 7.6 4.5, 4.6 4.3 C 4.9 9.4, 4.4 14.6, 4.7 19.7 C 7.7 19.9, 10.6 19.6, 13.6 19.8',
  'M9.8 12.1 C 13.2 11.9, 16.6 12.2, 20 12',
  'M16.8 8.6 C 17.8 9.8, 18.9 10.9, 20.2 12 C 18.9 13.2, 17.8 14.3, 16.8 15.4',
]);
const TICKET = hand([
  'M3.8 7.2 C 9.2 6.9, 14.8 7.4, 20.2 7.1 L 20.1 10 C 18.8 10.4, 18.6 13.6, 20.1 14 L 20.2 16.9 C 14.8 17.2, 9.2 16.7, 3.8 17 L 3.9 14.1 C 5.2 13.6, 5.4 10.4, 3.9 10 Z',
  'M14.4 7.4 C 14.5 8.4, 14.3 9.4, 14.4 10.4 M 14.4 13.6 C 14.5 14.6, 14.3 15.6, 14.4 16.6',
]);
const COPY = hand([
  'M8.6 8.4 C 12.4 8.2, 16.2 8.5, 19.8 8.3 C 20 12.2, 19.7 16.2, 19.9 20 C 16.2 20.2, 12.4 19.9, 8.7 20.1 C 8.9 16.2, 8.4 12.2, 8.6 8.4 Z',
  'M15.4 5.6 C 15.4 4.9, 15.2 4.2, 15.2 3.8 C 11.6 3.9, 8 3.7, 4.3 3.9 C 4.5 7.6, 4.2 11.4, 4.4 15.2 C 5 15.3, 5.6 15.3, 6.2 15.3',
]);
const DOWNLOAD = hand([
  'M11.9 4 C 12.2 7.8, 11.8 11.6, 12.1 15.2',
  'M7.6 11 C 9 12.4, 10.6 13.8, 12 15.4 C 13.4 13.8, 15 12.4, 16.4 11',
  'M4.4 16.6 C 4.4 17.8, 4.6 19, 4.6 20 C 9.6 20.3, 14.6 19.8, 19.6 20.1 C 19.6 19, 19.4 17.8, 19.6 16.6',
]);
const INVITE = hand([
  'M10 4.2 C 12 4.2, 13.4 5.8, 13.3 7.8 C 13.2 9.8, 11.8 11.2, 10 11.2 C 8 11.2, 6.6 9.7, 6.6 7.7 C 6.6 5.7, 8 4.3, 10.4 4.3',
  'M3.4 20 C 3.6 16, 6.4 13.6, 10 13.6 C 12 13.6, 13.6 14.2, 14.8 15.2',
  'M18.4 12.2 C 18.2 14.6, 18.5 17, 18.3 19.4',
  'M15 15.9 C 16.2 15.7, 19.4 16, 21.6 15.8',
]);
const BOT = hand([
  'M5.4 8.8 C 9.8 8.5, 14.2 9, 18.6 8.7 C 18.9 12.4, 18.5 16.2, 18.7 19.8 C 14.2 20.1, 9.8 19.7, 5.3 19.9 C 5.6 16.2, 5.2 12.4, 5.4 8.8 Z',
  'M12 8.6 L 12.1 5.2',
  { circle: [12, 4.2, 1], fill: true },
  { circle: [9.2, 13.4, 1.2], fill: true },
  { circle: [14.8, 13.4, 1.2], fill: true },
  'M9.6 16.8 C 11.2 17.2, 12.8 17.2, 14.4 16.8',
]);
const CROWN = hand([
  'M4 18.4 C 3.6 14, 3.4 10, 3.8 6.8 C 6 9.4, 7.8 11, 9 11.8 C 10 9.4, 11 7, 12 4.6 C 13 7, 14 9.4, 15 11.8 C 16.4 10.8, 18.2 9.2, 20.2 6.8 C 20.6 10.4, 20.4 14.4, 20 18.4 C 14.6 18.8, 9.4 18.6, 4 18.4 Z',
]);
const PODIUM = hand([
  'M8.6 9.4 C 10.8 9.2, 13.2 9.5, 15.4 9.3 L 15.5 20.2 L 8.5 20.3 Z',
  'M3.8 13.4 C 5.4 13.2, 7 13.5, 8.6 13.3 L 8.5 20.3 L 3.7 20.2 Z',
  'M15.5 15.4 C 17.1 15.2, 18.7 15.5, 20.3 15.3 L 20.2 20.2 L 15.5 20.2 Z',
  'M11.4 6.2 C 11.9 5.6, 12.4 5.1, 12.9 4.6 L 12.8 7.4',
]);
const RANDOM = hand([
  'M4.8 4.6 C 9.6 4.3, 14.4 4.8, 19.2 4.5 C 19.5 9.4, 19.1 14.4, 19.4 19.3 C 14.4 19.6, 9.6 19.2, 4.6 19.4 C 4.9 14.4, 4.5 9.4, 4.8 4.6 Z',
  { circle: [8.6, 8.6, 1.2], fill: true },
  { circle: [12, 12, 1.2], fill: true },
  { circle: [15.4, 15.4, 1.2], fill: true },
]);

/** Every meaning Crayon draws itself. The rest are Clean's, wobbled. */
export const CRAYON_ICONS: Partial<Record<IconName, Drawing>> = {
  home: SHIRT,
  matchup: FOOTBALL,
  sport: FOOTBALL,
  trophy: TROPHY,
  person: PERSON,
  people: PEOPLE,
  more: LINES,
  list: LINES,
  notifications: BELL,
  previous: PREVIOUS,
  next: NEXT,
  expand: EXPAND,
  draft: VS,
  scoreboard: BARS,
  recap: RECAP,
  news: NEWS,
  waivers: WAIVERS,
  trade: TRADE,
  standings: STANDINGS,
  when: CALENDAR,
  calendar: CALENDAR,
  history: HISTORY,
  rules: BOOK,
  looks: PALETTE,
  chat: CHAT,
  search: SEARCH,
  leagues: GRID,
  add: PLUS,
  remove: MINUS,
  close: CROSS,
  decline: CROSS,
  withdraw: CROSS,
  accept: CHECK,
  edit: PENCIL,
  live: BOLT,
  play: PLAY,
  pause: PAUSE,
  locked: LOCK,
  warning: WARNING,
  info: INFO,
  favorite: STAR,
  favorited: STAR_FILLED,
  back: BACK,
  forward: FORWARD,
  up: UP,
  down: DOWN,
  pending: CLOCK,
  send: SEND,
  delete: TRASH,
  undo: UNDO,
  refresh: REFRESH,
  share: SHARE,
  signout: LOGOUT,
  ticket: TICKET,
  copy: COPY,
  download: DOWNLOAD,
  invite: INVITE,
  bot: BOT,
  crown: CROWN,
  podium: PODIUM,
  random: RANDOM,
};
